import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Modal, ModalBody, ModalHeader } from "reactstrap";
import * as FaIcons from "react-icons/fa";

const normalizeText = (value) => String(value || "").toLowerCase().trim();

const DashboardRankingModal = ({
    isOpen,
    title,
    items,
    columns,
    searchPlaceholder,
    emptyLabel,
    closeLabel,
    loading,
    loadingLabel,
    loadingMore,
    loadMoreLabel,
    errorMessage,
    getSearchText,
    hasNext,
    onLoadMore,
    onSearchChange,
    onClose
}) => {
    const [searchText, setSearchText] = useState("");
    const remoteSearch = typeof onSearchChange === "function";

    useEffect(() => {
        if (isOpen) {
            setSearchText("");
        }
    }, [isOpen]);

    const filteredItems = useMemo(() => {
        if (remoteSearch) return items;

        const normalizedSearch = normalizeText(searchText);
        if (!normalizedSearch) return items;

        return items.filter((item) => normalizeText(getSearchText(item)).includes(normalizedSearch));
    }, [getSearchText, items, remoteSearch, searchText]);

    const handleSearchChange = (event) => {
        const value = event.target.value;
        setSearchText(value);

        if (remoteSearch) {
            onSearchChange(value);
        }
    };

    const handleScroll = useCallback((event) => {
        if (!hasNext || loading || loadingMore || !onLoadMore) return;

        const { scrollHeight, scrollTop, clientHeight } = event.currentTarget;
        if (scrollHeight - scrollTop - clientHeight <= 80) {
            onLoadMore();
        }
    }, [hasNext, loading, loadingMore, onLoadMore]);

    return (
        <Modal
            isOpen={isOpen}
            toggle={onClose}
            modalTransition={{ timeout: 0 }}
            backdropTransition={{ timeout: 0 }}
            size="lg"
        >
            <ModalHeader className="modalHeader dashboard-ranking-modal-header">
                {title}
                <button className="close" data-dismiss="close" onClick={onClose} type="button">
                    {closeLabel}
                </button>
            </ModalHeader>
            <ModalBody className="modalBody dashboard-ranking-modal-body">
                <div className="dashboard-ranking-search">
                    <FaIcons.FaSearch />
                    <input
                        className="form-control inputStandard"
                        value={searchText}
                        placeholder={searchPlaceholder}
                        onChange={handleSearchChange}
                    />
                </div>

                <div className="dashboard-ranking-table">
                    <div className="dashboard-ranking-table-head" style={{ gridTemplateColumns: columns.map((column) => column.width || "1fr").join(" ") }}>
                        {columns.map((column) => (
                            <span key={column.key} className={column.align === "right" ? "align-right" : ""}>
                                {column.label}
                            </span>
                        ))}
                    </div>
                    <div className="dashboard-ranking-table-scroll" onScroll={handleScroll}>
                        {loading && <div className="dashboard-empty-row">{loadingLabel}</div>}
                        {!loading && errorMessage && <div className="dashboard-empty-row dashboard-danger-text">{errorMessage}</div>}
                        {!loading && !errorMessage && filteredItems.length === 0 && <div className="dashboard-empty-row">{emptyLabel}</div>}
                        {!loading && !errorMessage && filteredItems.map((item, index) => (
                            <div
                                className="dashboard-ranking-table-row"
                                key={item.id || item.key || index}
                                style={{ gridTemplateColumns: columns.map((column) => column.width || "1fr").join(" ") }}
                            >
                                {columns.map((column) => (
                                    <span key={column.key} className={column.align === "right" ? "align-right" : ""}>
                                        {column.render ? column.render(item, index) : item[column.key]}
                                    </span>
                                ))}
                            </div>
                        ))}
                        {!loading && !errorMessage && loadingMore && <div className="dashboard-empty-row dashboard-ranking-load-more">{loadMoreLabel}</div>}
                    </div>
                </div>
            </ModalBody>
        </Modal>
    );
};

DashboardRankingModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    title: PropTypes.string.isRequired,
    items: PropTypes.array,
    columns: PropTypes.arrayOf(PropTypes.shape({
        key: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        width: PropTypes.string,
        align: PropTypes.string,
        render: PropTypes.func
    })).isRequired,
    searchPlaceholder: PropTypes.string.isRequired,
    emptyLabel: PropTypes.string.isRequired,
    closeLabel: PropTypes.string,
    loading: PropTypes.bool,
    loadingLabel: PropTypes.string,
    loadingMore: PropTypes.bool,
    loadMoreLabel: PropTypes.string,
    errorMessage: PropTypes.string,
    getSearchText: PropTypes.func.isRequired,
    hasNext: PropTypes.bool,
    onLoadMore: PropTypes.func,
    onSearchChange: PropTypes.func,
    onClose: PropTypes.func.isRequired
};

DashboardRankingModal.defaultProps = {
    items: [],
    closeLabel: "x",
    loading: false,
    loadingLabel: "Loading...",
    loadingMore: false,
    loadMoreLabel: "Loading...",
    errorMessage: "",
    hasNext: false,
    onLoadMore: null,
    onSearchChange: null
};

export default DashboardRankingModal;
